
<template>
  <header class="flex justify-center items-center py-4 mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-4">
    <nav class="rounded-full py-2 px-8 border border-gray-200/50 bg-white/95 shadow-lg backdrop-blur-md">
      <div class="flex justify-between items-center">
        <div class="flex items-center space-x-6">
          <router-link to="/" class="flex items-center gap-2 text-xl font-bold text-brand-600">
              <img src="/logo.webp" alt="Logo" class="h-10 w-8" />
              Optyfind
          </router-link>
          <div class="flex space-x-6">
            <router-link
              to="/"
              class="text-gray-500 hover:text-gray-700 text-sm font-medium"
              active-class="text-brand-600"
            >
              Application tracker
            </router-link>
            <router-link
              to="/my-skills"
              class="text-gray-500 hover:text-gray-700 text-sm font-medium"
              active-class="text-brand-600"
            >
              My Skills
            </router-link>
            <router-link
              to="/my-searches"
              class="text-gray-500 hover:text-gray-700 text-sm font-medium"
              active-class="text-brand-600"
            >
              Job Searches
            </router-link>
          </div>
        </div>

        <!-- User dropdown -->
        <div class="flex items-center ml-8 relative" ref="dropdownRef">
          <button
            @click="toggleDropdown"
            class="flex items-center gap-1.5 text-gray-700 text-sm font-medium hover:text-gray-900 focus:outline-none"
          >
            {{ userName }}
            <!-- Chevron down icon -->
            <svg
              class="h-4 w-4 text-gray-400 transition-transform duration-150"
              :class="{ 'rotate-180': isOpen }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <!-- Dropdown menu -->
          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <div
              v-if="isOpen"
              class="absolute right-0 top-full mt-2 w-44 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50"
            >
              <router-link
                to="/settings"
                @click="closeDropdown"
                class="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
              >
                <!-- User/settings icon -->
                <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Settings
              </router-link>
              <router-link
                to="/subscriptions"
                @click="closeDropdown"
                class="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
              >
                <!-- Star icon -->
                <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
                Subscriptions
              </router-link>
              <router-link
                to="/billing"
                @click="closeDropdown"
                class="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
              >
                <!-- Credit card icon -->
                <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                Billing
              </router-link>

              <router-link
                to="/support"
                @click="closeDropdown"
                class="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
              >
                <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                Support
              </router-link>

              <!-- Divider -->
              <div class="my-1 border-t border-gray-100"></div>

              <button
                @click="handleLogout"
                class="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
              >
                <!-- Logout icon -->
                <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          </transition>
        </div>
      </div>
    </nav>
  </header>
</template>

<script lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';

export default {
  name: 'TopNavigation',
  setup() {
    const router = useRouter();
    const authStore = useAuthStore();
    const isOpen = ref<boolean>(false);
    const dropdownRef = ref<HTMLElement | null>(null);

    const userName = computed<string>(() => {
      const u = authStore.user;
      return u?.first_name || u?.email || 'User';
    });

    const toggleDropdown = (): void => {
      isOpen.value = !isOpen.value;
    };

    const closeDropdown = (): void => {
      isOpen.value = false;
    };

    const handleLogout = async (): Promise<void> => {
      closeDropdown();
      await authStore.logout();
      router.push('/login');
    };

    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
        isOpen.value = false;
      }
    };

    onMounted(() => {
      document.addEventListener('mousedown', handleClickOutside);
    });

    onUnmounted(() => {
      document.removeEventListener('mousedown', handleClickOutside);
    });

    return {
      userName,
      isOpen,
      dropdownRef,
      toggleDropdown,
      closeDropdown,
      handleLogout,
    };
  },
};
</script>
